import { toRp } from "helpers/general_helper";
import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  Card,
  Typography,
  Form,
  Input,
  Button,
  Select,
  Checkbox,
  DatePicker,
  InputNumber,
  Divider,
  message,
  Spin,
} from "antd";
import { useHistory } from "react-router-dom";
import { KASIR_PREFIX_PATH } from "configs/AppConfig";
import { handlePost } from "redux/actions/baseAction";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || "/api";

const AddPinjamanPOS = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchingMember, setSearchingMember] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [transactionItems, setTransactionItems] = useState([]);
  const [totalPinjaman, setTotalPinjaman] = useState(0);
  const [userId, setUserId] = useState(null);

  const searchTimerRef = useRef(null);
  const productsFetchedRef = useRef(false);
  const isMountedRef = useRef(true);
  const cancelTokenRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      if (cancelTokenRef.current) {
        cancelTokenRef.current.abort?.();
      }
    };
  }, []);

  const fetchWithAuth = async (endpoint, options = {}) => {
    const token = localStorage.getItem("token");
    const url = `${API_BASE_URL}${API_PREFIX}/${endpoint}`;

    const controller = new AbortController();
    cancelTokenRef.current = controller;

    const defaultOptions = {
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "Content-Type": "application/json",
        ...options.headers,
      },
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw {
          status: response.status,
          data: errorData,
          message:
            errorData.message || `HTTP error! status: ${response.status}`,
        };
      }

      return await response.json();
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      }
      console.error("Fetch error:", error);
      throw error;
    }
  };

  const fetchProducts = async () => {
    if (
      productsFetchedRef.current ||
      loadingProducts ||
      !isMountedRef.current
    ) {
      return;
    }

    setLoadingProducts(true);
    try {
      const data = await fetchWithAuth("pos/pos/cashier/stock/list");

      if (!isMountedRef.current || !data) return;

      if (data && data.data) {
        if (Array.isArray(data.data)) {
          setProducts(data.data);
        } else if (data.data.items && Array.isArray(data.data.items)) {
          setProducts(data.data.items);
        } else {
          setProducts([]);
        }
        productsFetchedRef.current = true;
      } else if (Array.isArray(data)) {
        setProducts(data);
        productsFetchedRef.current = true;
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      if (isMountedRef.current) {
        if (error.status === 400) {
          console.warn("Products API returned 400. This might be expected.");
        } else {
          message.warning(
            "Gagal mengambil data produk. Silakan coba lagi nanti.",
          );
        }
        setProducts([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingProducts(false);
      }
    }
  };

  const handleSearchMember = (value) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!value || value.trim().length === 0) {
      if (isMountedRef.current) {
        setMemberData(null);
        setUserId(null);
        form.setFieldsValue({
          nama: "",
        });
      }
      return;
    }

    searchTimerRef.current = setTimeout(async () => {
      await performSearch(value.trim());
    }, 500);
  };

  const performSearch = async (value) => {
    if (value.length < 2 || !isMountedRef.current) {
      return;
    }

    setSearchingMember(true);
    try {
      const data = await fetchWithAuth(
        `pos/pos/cashier/member/by-registration/${value}`,
      );

      if (!isMountedRef.current || !data) return;

      let member = null;
      if (data && data.data) {
        member = data.data;
      } else if (data && data.id) {
        member = data;
      }

      if (member) {
        if (!member.user_id) {
          message.error(
            "Member ditemukan tetapi belum terhubung ke akun user (user_id kosong). Hubungi admin.",
          );
          setMemberData(null);
          setUserId(null);
          form.setFieldsValue({ nama: "" });
          return;
        }
        setMemberData(member);
        setUserId(member.user_id);
        form.setFieldsValue({
          nama: member.name || "",
        });
        message.success("Anggota ditemukan");

        await fetchProducts();
      } else {
        setMemberData(null);
        setUserId(null);
        form.setFieldsValue({
          nama: "",
        });
        message.warning("Anggota tidak ditemukan");
      }
    } catch (error) {
      console.error("Error searching member:", error);
      if (!isMountedRef.current) return;

      setMemberData(null);
      setUserId(null);
      form.setFieldsValue({
        nama: "",
      });

      if (error.status === 404) {
        message.warning("Anggota tidak ditemukan");
      } else if (error.name !== "AbortError") {
        message.error(error.message || "Gagal mencari anggota");
      }
    } finally {
      if (isMountedRef.current) {
        setSearchingMember(false);
      }
    }
  };

  const handleProductSelect = (value, index) => {
    const product = products.find((p) => p.id === value);
    if (product) {
      const newItems = [...transactionItems];
      newItems[index] = {
        ...newItems[index],
        item_id: product.id,
        name: product.name || product.item_name || product.product_name || "",
        price:
          product.price || product.selling_price || product.sale_price || 0,
        quantity: newItems[index]?.quantity || 1,
        total:
          (product.price || product.selling_price || product.sale_price || 0) *
          (newItems[index]?.quantity || 1),
      };
      setTransactionItems(newItems);
      calculateTotal(newItems);
    }
  };

  const handleQuantityChange = (value, index) => {
    const newItems = [...transactionItems];
    const price = newItems[index]?.price || 0;
    newItems[index] = {
      ...newItems[index],
      quantity: value || 1,
      total: price * (value || 1),
    };
    setTransactionItems(newItems);
    calculateTotal(newItems);
  };

  const calculateTotal = (items) => {
    const total = items.reduce((sum, item) => sum + (item.total || 0), 0);
    setTotalPinjaman(total);
    form.setFieldsValue({
      jumlahPinjaman: total,
    });
  };

  const handleJumlahPinjamanChange = (value) => {
    const numValue = parseInt(value) || 0;
    setTotalPinjaman(numValue);
    form.setFieldsValue({
      jumlahPinjaman: numValue,
    });
  };

  const addProductRow = async () => {
    if (!productsFetchedRef.current && !loadingProducts) {
      await fetchProducts();
    }

    if (!isMountedRef.current) return;

    setTransactionItems([
      ...transactionItems,
      {
        id: Date.now(),
        item_id: null,
        name: "",
        price: 0,
        quantity: 1,
        total: 0,
      },
    ]);
  };

  const removeProductRow = (index) => {
    const newItems = transactionItems.filter((_, i) => i !== index);
    setTransactionItems(newItems);
    calculateTotal(newItems);
  };

  const handleSubmit = async (values) => {
    if (!userId) {
      message.error("Silakan cari dan pilih anggota terlebih dahulu");
      return;
    }

    if (transactionItems.length === 0) {
      message.error("Silakan tambahkan minimal 1 produk");
      return;
    }

    const hasEmptyProduct = transactionItems.some((item) => !item.item_id);
    if (hasEmptyProduct) {
      message.error("Silakan pilih produk untuk semua item");
      return;
    }

    setLoading(true);
    try {
      const tenor = values.tenor || 1;
      const paidTenure = values.tenorBerjalan || 0;
      const angsuranPerBulan = Math.round(totalPinjaman / tenor);
      const paidAmount = angsuranPerBulan * paidTenure;
      const remainingAmount = totalPinjaman - paidAmount;
      const remainingTenure = tenor - paidTenure;

      const items = transactionItems.map((item) => ({
        item_id: item.item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
      }));

      const payload = {
        loans: [
          {
            user_id: userId,
            registration_number: memberData?.registration_number || values.nomorKeanggotaan || null,
            items: items,
            total_amount: totalPinjaman,
            tenure: tenor,
            paid_tenure: paidTenure,
            paid_amount: paidAmount,
            remaining_tenure: remainingTenure,
            remaining_amount: remainingAmount,
            period_type: values.periodType || "MONTHLY",
            contract_start: values.contractDate
              ? moment(values.contractDate[0]).format("YYYY-MM-DD")
              : null,
            contract_end: values.contractDate
              ? moment(values.contractDate[1]).format("YYYY-MM-DD")
              : null,
            pos_transaction_id: null,
          },
        ],
      };

      const response = await new Promise((resolve, reject) => {
        handlePost(
          "pos/pos/cashier/pos-loan/record",
          payload,
          (data, success) => {
            if (!isMountedRef.current) {
              return;
            }

            if (success) {
              resolve(data);
            } else {
              reject(new Error(data?.message || "Gagal menyimpan data"));
            }
          },
          false,
        );
      });

      if (response && isMountedRef.current) {
        message.success("Data pinjaman berhasil ditambahkan");
        history.push(`${KASIR_PREFIX_PATH}/pinjaman-pos`);
      }
    } catch (error) {
      console.error("Submit error:", error);
      if (isMountedRef.current) {
        message.error(error.message || "Gagal menambahkan data pinjaman");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    history.goBack();
  };

  const inputStyle = {
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#374151",
  };

  const getAngsuranPerBulan = () => {
    const tenor = form.getFieldValue("tenor") || 1;
    if (totalPinjaman > 0 && tenor > 0) {
      return Math.round(totalPinjaman / tenor);
    }
    return 0;
  };

  const getSisaKewajiban = () => {
    const tenor = form.getFieldValue("tenor") || 1;
    const tenorBerjalan = form.getFieldValue("tenorBerjalan") || 0;
    if (totalPinjaman > 0 && tenor > 0) {
      const angsuran = Math.round(totalPinjaman / tenor);
      return totalPinjaman - angsuran * tenorBerjalan;
    }
    return totalPinjaman;
  };

  return (
    <div
      style={{
        padding: "24px",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          type="text"
          style={{ fontSize: "20px" }}
        />
        <Title level={3} style={{ margin: 0 }}>
          Tambah Data Pinjaman POS Anggota
        </Title>
      </div>

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            periodType: "MONTHLY",
            tenor: 1,
            tenorBerjalan: 0,
            sisaTenor: 1,
            isPaidOff: false,
            jumlahPinjaman: 0,
          }}
        >
          <Card
            bordered={false}
            style={{
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col xs={24} md={12}>
                <Text style={labelStyle}>ID Anggota *</Text>
                <Form.Item
                  name="nomorKeanggotaan"
                  rules={[
                    { required: true, message: "Masukkan ID Anggota" },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Masukkan ID Anggota atau registrasi"
                    size="large"
                    style={inputStyle}
                    onChange={(e) => handleSearchMember(e.target.value)}
                    suffix={searchingMember ? <Spin size="small" /> : null}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Text style={labelStyle}>Nama *</Text>
                <Form.Item
                  name="nama"
                  rules={[{ required: true, message: "Masukkan nama anggota" }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    placeholder="Nama akan terisi otomatis"
                    size="large"
                    style={inputStyle}
                    readOnly
                  />
                </Form.Item>
              </Col>
            </Row>

            <Text
              strong
              style={{
                fontSize: "15px",
                display: "block",
                marginBottom: "16px",
              }}
            >
              Daftar Transaksi Pinjaman
            </Text>

            {transactionItems.map((item, index) => (
              <Row
                gutter={[16, 16]}
                key={item.id || index}
                style={{
                  marginBottom: "16px",
                  padding: "16px",
                  backgroundColor: "#fafafa",
                  borderRadius: "8px",
                }}
                align="middle"
              >
                <Col xs={24} md={6}>
                  <Text style={labelStyle}>Produk *</Text>
                  <Select
                    placeholder="Pilih"
                    style={{ width: "100%" }}
                    size="large"
                    loading={loadingProducts}
                    value={item.item_id}
                    onChange={(value) => handleProductSelect(value, index)}
                    showSearch
                    optionFilterProp="children"
                    notFoundContent={
                      loadingProducts ? (
                        <Spin size="small" />
                      ) : (
                        "Tidak ada produk tersedia"
                      )
                    }
                  >
                    {products.map((product) => (
                      <Option key={product.id} value={product.id}>
                        {product.name ||
                          product.item_name ||
                          product.product_name ||
                          "Produk"}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} md={4}>
                  <Text style={labelStyle}>Harga Jual</Text>
                  <Input
                    value={`Rp${toRp(item.price || 0)}`}
                    readOnly
                    size="large"
                    style={inputStyle}
                  />
                </Col>
                <Col xs={24} md={4}>
                  <Text style={labelStyle}>Qty *</Text>
                  <InputNumber
                    min={1}
                    value={item.quantity || 1}
                    onChange={(value) => handleQuantityChange(value, index)}
                    size="large"
                    style={{ width: "100%" }}
                  />
                </Col>
                <Col xs={24} md={6}>
                  <Text style={labelStyle}>Total Harga Jual</Text>
                  <Input
                    value={`Rp${toRp(item.total || 0)}`}
                    readOnly
                    size="large"
                    style={{
                      ...inputStyle,
                      fontWeight: 600,
                      color: "#1d4ed8",
                    }}
                  />
                </Col>
                <Col xs={24} md={4} style={{ textAlign: "center" }}>
                  <Button
                    type="link"
                    icon={<MinusCircleOutlined />}
                    danger
                    onClick={() => removeProductRow(index)}
                    style={{ fontSize: "20px" }}
                  >
                    Hapus
                  </Button>
                </Col>
              </Row>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addProductRow}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "8px",
                borderColor: "#d1d5db",
                color: "#6b7280",
              }}
            >
              ITEM LAINNYA
            </Button>

            <Divider style={{ margin: "24px 0" }} />

            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Jumlah Pinjaman</Text>
                <Form.Item name="jumlahPinjaman" style={{ marginBottom: 0 }}>
                  <InputNumber
                    min={0}
                    placeholder="Masukkan jumlah pinjaman"
                    size="large"
                    style={{ width: "100%", ...inputStyle }}
                    onChange={handleJumlahPinjamanChange}
                    formatter={(value) =>
                      `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                    }
                    parser={(value) =>
                      value.replace(/Rp\s?|(\.*)/g, "").replace(/,/g, "")
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Bulan Tenor Berjalan</Text>
                <Form.Item name="tenorBerjalan" style={{ marginBottom: 0 }}>
                  <InputNumber
                    min={0}
                    style={{ width: "100%", ...inputStyle }}
                    size="large"
                    onChange={(value) => {
                      const tenor = form.getFieldValue("tenor") || 1;
                      form.setFieldsValue({
                        sisaTenor: Math.max(0, tenor - (value || 0)),
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Sisa Tenor</Text>
                <Form.Item name="sisaTenor" style={{ marginBottom: 0 }}>
                  <InputNumber
                    min={0}
                    style={{ width: "100%", ...inputStyle }}
                    size="large"
                    readOnly
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Tenor</Text>
                <Form.Item
                  name="tenor"
                  rules={[{ required: true, message: "Masukkan tenor" }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    min={1}
                    style={{ width: "100%", ...inputStyle }}
                    size="large"
                    onChange={(value) => {
                      const tenorBerjalan =
                        form.getFieldValue("tenorBerjalan") || 0;
                      form.setFieldsValue({
                        sisaTenor: Math.max(0, (value || 1) - tenorBerjalan),
                      });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Text style={labelStyle}>Range Tanggal Kontrak Pinjaman *</Text>
                <Form.Item
                  name="contractDate"
                  rules={[
                    { required: true, message: "Pilih range tanggal kontrak" },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <RangePicker
                    size="large"
                    style={{ width: "100%", ...inputStyle }}
                    format="DD/MM/YYYY"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Tipe Periode</Text>
                <Form.Item name="periodType" style={{ marginBottom: 0 }}>
                  <Select size="large" style={inputStyle}>
                    <Option value="MONTHLY">Bulanan</Option>
                    <Option value="WEEKLY">Mingguan</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Text style={labelStyle}>Sudah Lunas</Text>
                <Form.Item
                  name="isPaidOff"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox>Sudah lunas</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "24px 0" }} />

            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Text style={labelStyle}>Angsuran Per Bulan</Text>
                <Input
                  value={`Rp${toRp(getAngsuranPerBulan())}`}
                  readOnly
                  size="large"
                  style={{
                    ...inputStyle,
                    fontWeight: 600,
                    color: "#1d4ed8",
                    fontSize: "16px",
                  }}
                />
              </Col>
              <Col xs={24} md={12}>
                <Text style={labelStyle}>Sisa Kewajiban</Text>
                <Input
                  value={`Rp${toRp(getSisaKewajiban())}`}
                  readOnly
                  size="large"
                  style={{
                    ...inputStyle,
                    fontWeight: 600,
                    color: "#dc2626",
                    fontSize: "16px",
                  }}
                />
              </Col>
            </Row>
          </Card>

          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "16px",
            }}
          >
            <Button
              size="large"
              onClick={handleCancel}
              style={{
                borderRadius: "8px",
                padding: "0 32px",
                fontWeight: 600,
              }}
            >
              BATAL
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{
                backgroundColor: "#2563eb",
                borderRadius: "8px",
                padding: "0 32px",
                fontWeight: 600,
              }}
            >
              SIMPAN
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default AddPinjamanPOS;
